import { Attachment, AttachmentFile } from "@/types/Attachment";
import {
  createTemplateAttachment as createTemplateAttachmentApi,
  getTemplateAttachments as getTemplateAttachmentsApi,
  getTemplateAttachment as getTemplateAttachmentApi,
  updateTemplateAttachment as updateTemplateAttachmentApi,
  deleteTemplateAttachment as deleteTemplateAttachmentApi,
  presignTemplateFileUpload as presignTemplateFileUploadApi,
  confirmTemplateFileUpload as confirmTemplateFileUploadApi,
  deleteTemplateFile as deleteTemplateFileApi,
  getTemplateFileDownloadUrl as getTemplateFileDownloadUrlApi,
} from "@/lib/api/clients/TemplateAttachmentClient";
import { handleErrorServer, ServerError } from "@/lib/api/errors/ErrorHandler";
import { isValidObjectId } from "@/lib/utils/input-validator";

export async function getTemplateAttachmentsHandler(
  templateId: string,
): Promise<Attachment[] | ServerError> {
  try {
    if (!isValidObjectId(templateId)) throw new Error("validationError");
    return await getTemplateAttachmentsApi(templateId);
  } catch (error) {
    console.error("Error getting template attachments:", error);
    return handleErrorServer(error);
  }
}

export async function createTemplateAttachmentHandler(
  templateId: string,
  attachment: Partial<Attachment>,
  /** J6: the event an "import from event" copies from (`?sourceEventId=`). */
  sourceEventId?: string,
): Promise<string | ServerError> {
  try {
    if (!isValidObjectId(templateId)) throw new Error("validationError");
    if (sourceEventId !== undefined && !isValidObjectId(sourceEventId)) throw new Error("validationError");
    return await createTemplateAttachmentApi(templateId, attachment, sourceEventId);
  } catch (error) {
    console.error("Error creating template attachment:", error);
    return handleErrorServer(error);
  }
}

export async function updateTemplateAttachmentHandler(
  templateId: string,
  attachmentId: string,
  attachment: Partial<Attachment>,
): Promise<void | ServerError> {
  try {
    if (!isValidObjectId(templateId)) throw new Error("validationError");
    if (!isValidObjectId(attachmentId)) throw new Error("validationError");
    return await updateTemplateAttachmentApi(templateId, attachmentId, attachment);
  } catch (error) {
    console.error("Error updating template attachment:", error);
    return handleErrorServer(error);
  }
}

export async function deleteTemplateAttachmentHandler(
  templateId: string,
  attachmentId: string,
): Promise<void | ServerError> {
  try {
    if (!isValidObjectId(templateId)) throw new Error("validationError");
    if (!isValidObjectId(attachmentId)) throw new Error("validationError");
    return await deleteTemplateAttachmentApi(templateId, attachmentId);
  } catch (error) {
    console.error("Error deleting template attachment:", error);
    return handleErrorServer(error);
  }
}

// ── File Management (mirrors AttachmentHandling; error codes match the
//    event side: presignNotFound, fileSizeMismatch, storageCapExceeded,
//    fileNotFound. Template files are never guest-scoped) ──

export async function getTemplateAttachmentHandler(
  templateId: string,
  attachmentId: string,
): Promise<Attachment | ServerError> {
  try {
    if (!isValidObjectId(templateId)) throw new Error("validationError");
    if (!isValidObjectId(attachmentId)) throw new Error("validationError");
    return await getTemplateAttachmentApi(templateId, attachmentId);
  } catch (error) {
    console.error("Error getting template attachment:", error);
    return handleErrorServer(error);
  }
}

export async function presignTemplateFileUploadHandler(
  templateId: string,
  attachmentId: string,
  data: { fileName: string; contentType: string; fileSize: number },
): Promise<{ uploadUrl: string; s3Key: string } | ServerError> {
  try {
    if (!isValidObjectId(templateId)) throw new Error("validationError");
    if (!isValidObjectId(attachmentId)) throw new Error("validationError");
    return await presignTemplateFileUploadApi(templateId, attachmentId, data);
  } catch (error) {
    console.error("Error presigning template file upload:", error);
    return handleErrorServer(error);
  }
}

export async function confirmTemplateFileUploadHandler(
  templateId: string,
  attachmentId: string,
  data: { s3Key: string; fileName: string; fileSize: number; contentType: string },
): Promise<AttachmentFile | ServerError> {
  try {
    if (!isValidObjectId(templateId)) throw new Error("validationError");
    if (!isValidObjectId(attachmentId)) throw new Error("validationError");
    return await confirmTemplateFileUploadApi(templateId, attachmentId, data);
  } catch (error) {
    console.error("Error confirming template file upload:", error);
    return handleErrorServer(error);
  }
}

export async function deleteTemplateFileHandler(
  templateId: string,
  attachmentId: string,
  fileId: string,
): Promise<void | ServerError> {
  try {
    if (!isValidObjectId(templateId)) throw new Error("validationError");
    if (!isValidObjectId(attachmentId)) throw new Error("validationError");
    if (!isValidObjectId(fileId)) throw new Error("validationError");
    return await deleteTemplateFileApi(templateId, attachmentId, fileId);
  } catch (error) {
    console.error("Error deleting template file:", error);
    return handleErrorServer(error);
  }
}

export async function getTemplateFileDownloadUrlHandler(
  templateId: string,
  attachmentId: string,
  fileId: string,
): Promise<{ url: string } | ServerError> {
  try {
    if (!isValidObjectId(templateId)) throw new Error("validationError");
    if (!isValidObjectId(attachmentId)) throw new Error("validationError");
    if (!isValidObjectId(fileId)) throw new Error("validationError");
    return await getTemplateFileDownloadUrlApi(templateId, attachmentId, fileId);
  } catch (error) {
    console.error("Error getting template file download URL:", error);
    return handleErrorServer(error);
  }
}
